import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeRequestAuth } from '../../_config/api';
import { showErrorToast, showSuccessToast } from '../../utils/toastUtils';
import { ToastContainer } from 'react-toastify';
import { FiPhone, FiMail, FiCalendar, FiBriefcase, FiDollarSign, FiCheck, FiX, FiArrowLeft } from 'react-icons/fi';
import profileImg from "../../assets/img/profile.jpg";

export default function AgentProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [statsMode, setStatsMode] = useState('month'); // 'month' or 'total'
    const [activeFilter, setActiveFilter] = useState('recent'); // 'recent', 'today', '7days', 'thisMonth', 'custom'
    const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'history' or 'payouts'
    const [payoutLoading, setPayoutLoading] = useState(false);
    const [showSettleModal, setShowSettleModal] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [adminNotes, setAdminNotes] = useState("");
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [payoutMonth, setPayoutMonth] = useState(new Date().toISOString().split('-').slice(0, 2).join('-'));
    const [payoutStatusFilter, setPayoutStatusFilter] = useState('all'); // 'all', 'pending', 'paid'

    const fetchAgentDetails = async (date = null, range = null) => {
        try {
            setLoading(true);
            const res = await makeRequestAuth("post", "/admin/getAgentStats", { id, date, range });
            if (res?.error) {
                showErrorToast(res.message || "Failed to fetch agent details");
                return;
            }
            setData(res.data);
            if (date) {
                setSelectedDate(res.data.selectedDate);
                setActiveFilter('custom');
            } else if (range) {
                setActiveFilter(range);
            } else {
                setActiveFilter('recent');
            }
        } catch (err) {
            showErrorToast("Something went wrong");
        } finally {
            setLoading(false);
        }
    };


    const markAsComplete = async (jobId) => {
        if (!window.confirm("Mark this job as complete?")) return;
        try {
            setLoading(true);
            const res = await makeRequestAuth("post", "/admin/updateUserServiceStatus", { id: jobId, status: 'complete' });
            if (!res.error) {
                showSuccessToast("Job marked as complete");
                fetchAgentDetails(selectedDate, activeFilter !== 'recent' ? activeFilter : null);
            } else {
                showErrorToast(res.message || "Failed to update status");
                setLoading(false);
            }
        } catch (err) {
            showErrorToast("Something went wrong");
            setLoading(false);
        }
    };

    const handleGeneratePayout = async () => {
        try {
            setPayoutLoading(true);
            const res = await makeRequestAuth("post", "/admin/generateMonthlyPayout", { agentId: id, month: payoutMonth });
            if (!res.error) {
                showSuccessToast("Payout generated successfully");
                setShowPayoutModal(false);
                fetchAgentDetails();
            } else {
                showErrorToast(res.message || "No tasks found for this month");
            }
        } catch (err) {
            showErrorToast(err.message || "Something went wrong");
        } finally {
            setPayoutLoading(false);
        }
    };

    const handleUpdatePayoutStatus = async (payout, status, notes) => {
        try {
            setPayoutLoading(true);
            let payoutId = payout._id;

            // If it's a virtual record (tasks exist but no payout record yet)
            if (payout.isVirtual) {
                const genRes = await makeRequestAuth("post", "/admin/generateMonthlyPayout", { agentId: id, month: payout.month });
                if (genRes.error) {
                    showErrorToast(genRes.message || "Failed to generate record");
                    return;
                }
                payoutId = genRes.data._id;
            }

            // Update status to paid with notes
            const res = await makeRequestAuth("post", "/admin/updateAgentPayoutStatus", {
                payoutId,
                status,
                notes,
                transactionId: `AUTO-${Date.now()}` // Automatic transaction ID
            });

            if (!res.error) {
                showSuccessToast(`Payout marked as ${status}`);
                setShowSettleModal(false);
                fetchAgentDetails();
            } else {
                showErrorToast(res.message || "Failed to update payout");
            }
        } catch (err) {
            console.error("Payout update error:", err);
            showErrorToast(err.response?.data?.message || err.message || "Something went wrong");
        } finally {
            setPayoutLoading(false);
        }
    };

    useEffect(() => {
        fetchAgentDetails();
    }, [id]);

    if (loading && !data) return <div className="p-20 text-center font-bold text-gray-500 text-xl">Loading Agent Profile...</div>;
    if (!data) return <div className="p-20 text-center font-bold text-red-500 text-xl">Agent Not Found</div>;

    const { agent, totalDuties, history, inProgress, totalEarnings, monthlyStats, dateStats, displayTitle, payouts } = data;

    // 35% Calculation
    const agentShareTotal = (totalEarnings * 0.10).toFixed(2);
    const agentShareMonthly = (monthlyStats.earnings * 0.10).toFixed(2);

    return (
        <div className="p-2 md:py-4 md:px-6 min-h-screen bg-slate-50">
            <ToastContainer />

            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight uppercase italic">Agent Profile</h2>
                    <p className="text-xs text-slate-400 font-bold tracking-wider uppercase mt-1">View history & duties</p>
                </div>
                <button
                    onClick={() => navigate(-1)}
                    className="px-5 py-2 bg-white border border-slate-200 rounded-xl font-bold text-slate-600 hover:text-[#24ADD7] hover:border-[#24ADD7] transition-all shadow-sm active:scale-95 text-sm"
                >
                    ← Back
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                {/* LEFT COLUMN: BASIC INFO */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-[40px] p-6 shadow-xl shadow-slate-200/50 border border-white">
                        <div className="flex flex-col items-center text-center">
                            <div className="relative mb-4">
                                <img
                                    src={agent.image || profileImg}
                                    className="w-24 h-24 rounded-3xl object-cover border-4 border-slate-50 shadow-inner"
                                    alt="agent"
                                />
                                <div className="absolute -bottom-1 -right-1 bg-green-500 w-5 h-5 rounded-full border-[3px] border-white"></div>
                            </div>
                            <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">{agent.name}</h3>
                            <span className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-[9px] font-black uppercase tracking-widest mt-1.5">
                                {agent.serviceType?.name || "Service Agent"}
                            </span>
                        </div>

                        <div className="mt-6 space-y-4">
                            <div className="flex items-center gap-3 group">
                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-[#24ADD7] group-hover:text-white transition-all">
                                    <FiPhone size={14} />
                                </div>
                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Phone</p>
                                    <p className="text-sm font-bold text-slate-700">{agent.phone}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 group">
                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-[#24ADD7] group-hover:text-white transition-all">
                                    <FiMail size={14} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Email</p>
                                    <p className="text-sm font-bold text-slate-700 truncate">{agent.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 group">
                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-[#24ADD7] group-hover:text-white transition-all">
                                    <FiCalendar size={14} />
                                </div>
                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Joined On</p>
                                    <p className="text-sm font-bold text-slate-700">{new Date(agent.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN: STATS & TABBED VIEW */}
                <div className="lg:col-span-2 space-y-8">

                    {/* CONSOLIDATED PREMIUM STAT CARD */}
                    <div className="relative overflow-hidden bg-slate-900 rounded-[40px] p-5 text-white shadow-2xl shadow-slate-900/30 group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#24ADD7]/20 rounded-full blur-3xl -mr-16 -mt-16"></div>

                        <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                            <div>
                                <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1">Agent Financials</p>
                                <h4 className="text-2xl font-black italic tracking-tight">{statsMode === 'month' ? 'Monthly Performance' : 'Lifetime Overview'}</h4>
                            </div>

                            <div className="flex p-1 bg-white/5 rounded-2xl border border-white/10">
                                <button
                                    onClick={() => setStatsMode('month')}
                                    className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${statsMode === 'month' ? 'bg-[#24ADD7] text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => {
                                        setStatsMode('total');
                                        fetchAgentDetails(null, null);
                                    }}
                                    className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${statsMode === 'total' ? 'bg-[#24ADD7] text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                                >
                                    Lifetime
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative">
                            <div className="bg-white/5 p-3 rounded-3xl border border-white/5 hover:border-white/10 transition-all">
                                <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1">Tasks</p>
                                <p className="text-lg font-black text-white">{statsMode === 'month' ? monthlyStats.count : totalDuties}</p>
                            </div>
                            <div className="bg-white/5 p-3 rounded-3xl border border-white/5 hover:border-white/10 transition-all">
                                <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1">Revenue</p>
                                <p className="text-lg font-black text-white">₹{statsMode === 'month' ? monthlyStats.earnings : totalEarnings}</p>
                            </div>
                            <div className="md:col-span-2 bg-[#24ADD7]/10 p-3 rounded-3xl border border-[#24ADD7]/20 flex flex-col justify-center">
                                <p className="text-[8px] font-black text-[#24ADD7] uppercase tracking-[0.2em] mb-1">Agent Share (35%)</p>
                                <p className="text-2xl font-black text-green-400 tracking-tighter">₹{statsMode === 'month' ? agentShareMonthly : agentShareTotal}</p>
                            </div>
                        </div>
                    </div>

                    {/* TAB SELECTOR */}
                    <div className="flex gap-4 border-b border-slate-200">
                        <button
                            onClick={() => setActiveTab('pending')}
                            className={`pb-4 px-2 text-sm font-black uppercase tracking-widest transition-all border-b-2 ${activeTab === 'pending' ? 'border-[#24ADD7] text-[#24ADD7]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                        >
                            Pending Tasks
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`pb-4 px-2 text-sm font-black uppercase tracking-widest transition-all border-b-2 ${activeTab === 'history' ? 'border-[#24ADD7] text-[#24ADD7]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                        >
                            Job History
                        </button>
                        <button
                            onClick={() => setActiveTab('payouts')}
                            className={`pb-4 px-2 text-sm font-black uppercase tracking-widest transition-all border-b-2 ${activeTab === 'payouts' ? 'border-[#24ADD7] text-[#24ADD7]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                        >
                            Monthly Payouts
                        </button>
                    </div>

                    {activeTab === 'pending' ? (
                        <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                            <div className="px-8 py-6 border-b border-slate-50 flex items-center gap-4">
                                <div className="w-2 h-8 bg-orange-400 rounded-full"></div>
                                <h4 className="text-xl font-black text-slate-800 uppercase italic tracking-tight">Pending / In-Progress Tasks</h4>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                                            <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer</th>
                                            <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Service</th>
                                            <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                            <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {inProgress && inProgress.length > 0 ? inProgress.map((item) => (
                                            <tr key={item._id} className="hover:bg-slate-50/50 transition-colors group">
                                                <td className="px-8 py-5">
                                                    <p className="text-sm font-bold text-slate-700">{new Date(item.serviceDate).toLocaleDateString('en-GB')}</p>
                                                    <p className="text-[10px] text-slate-400 font-medium">{item.serviceTimeSlot}</p>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <p className="text-sm font-bold text-slate-700">{item.userId?.name || "N/A"}</p>
                                                    <p className="text-[10px] text-slate-400 font-medium">{item.userId?.phone || "N/A"}</p>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <p className="text-sm font-bold text-slate-700">{item.serviceType?.name || "Service"}</p>
                                                    <p className="text-xs font-bold text-[#24ADD7]">₹{item.serviceFee}</p>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-orange-100 text-orange-600">
                                                        {item.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-5 text-right">
                                                    <button
                                                        onClick={() => markAsComplete(item._id)}
                                                        className="px-4 py-2 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all shadow-md shadow-green-500/20"
                                                    >
                                                        Mark Complete
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="px-8 py-20 text-center">
                                                    <p className="text-slate-400 font-bold italic">No pending tasks found</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : activeTab === 'history' ? (
                        <>
                            {/* RECENT HISTORY TABLE WITH DATE SELECTOR */}
                            <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                                <div className="px-8 py-6 border-b border-slate-50 flex flex-col xl:flex-row justify-between items-center gap-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-2 h-8 bg-[#24ADD7] rounded-full"></div>
                                        <h4 className="text-xl font-black text-slate-800 uppercase italic tracking-tight">{displayTitle}</h4>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <div className="flex p-1 bg-slate-50 rounded-2xl border border-slate-100">
                                            {['today', '7days', 'thisMonth'].map((range) => (
                                                <button
                                                    key={range}
                                                    onClick={() => fetchAgentDetails(null, range)}
                                                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeFilter === range ? 'bg-white text-[#24ADD7] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                                >
                                                    {range === 'today' ? 'Today' : range === '7days' ? '7 Days' : 'Month'}
                                                </button>
                                            ))}
                                        </div>

                                        <input
                                            type="date"
                                            value={selectedDate}
                                            onChange={(e) => fetchAgentDetails(e.target.value)}
                                            className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 outline-none focus:border-[#24ADD7] transition-all"
                                        />

                                        {activeFilter !== 'recent' && (
                                            <button
                                                onClick={() => fetchAgentDetails()}
                                                className="px-4 py-2 bg-red-50 text-red-500 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-all"
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {data.isDateFiltered && (
                                    <div className="p-8 bg-[#24ADD7]/5 border-b border-slate-50 flex items-center justify-around text-center">
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tasks</p>
                                            <h5 className="text-3xl font-black text-slate-800 tracking-tight">{dateStats.count}</h5>
                                        </div>
                                        <div className="w-px h-12 bg-slate-200"></div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Earnings</p>
                                            <h5 className="text-3xl font-black text-[#24ADD7] tracking-tight">₹{dateStats.earnings}</h5>
                                        </div>
                                        <div className="w-px h-12 bg-slate-200"></div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Agent Share (35%)</p>
                                            <h5 className="text-3xl font-black text-green-600 tracking-tight">₹{(dateStats.earnings * 0.10).toFixed(2)}</h5>
                                        </div>
                                    </div>
                                )}

                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Service</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Earnings</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {history.length > 0 ? history.map((item) => (
                                                <tr key={item._id} className="hover:bg-slate-50/50 transition-colors group">
                                                    <td className="px-8 py-5">
                                                        <p className="text-sm font-bold text-slate-700">{new Date(item.serviceDate).toLocaleDateString('en-GB')}</p>
                                                        <p className="text-[10px] text-slate-400 font-medium">{item.serviceTimeSlot}</p>
                                                    </td>
                                                    <td className="px-8 py-5">
                                                        <p className="text-sm font-bold text-slate-700">{item.userId?.name || "N/A"}</p>
                                                        <p className="text-[10px] text-slate-400 font-medium">{item.userId?.phone || "N/A"}</p>
                                                    </td>
                                                    <td className="px-8 py-5">
                                                        <p className="text-sm font-bold text-slate-700">{item.serviceType?.name || "Service"}</p>
                                                    </td>
                                                    <td className="px-8 py-5">
                                                        <span className="text-sm font-black text-[#24ADD7]">₹{item.serviceFee}</span>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="4" className="px-8 py-20 text-center">
                                                        <div className="flex flex-col items-center justify-center">
                                                            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-200 mb-4">
                                                                <FiBriefcase size={32} />
                                                            </div>
                                                            <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">No duty history found</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-6">
                            {/* PAYOUTS MANAGEMENT */}
                            <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                                <div className="px-8 py-6 border-b border-slate-50 flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <div className="w-2 h-8 bg-green-500 rounded-full"></div>
                                        <h4 className="text-xl font-black text-slate-800 uppercase italic tracking-tight">Monthly Payouts</h4>
                                    </div>
                                    <p className="text-xs text-slate-400 font-medium">Automatic billing based on completed tasks</p>
                                </div>

                                {showSettleModal && (
                                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                                        <div className="bg-white rounded-[40px] p-8 w-full max-w-md shadow-2xl border border-white relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#24ADD7]/10 rounded-full blur-3xl -mr-16 -mt-16"></div>

                                            <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tight mb-2">Settle Payout</h3>
                                            <p className="text-slate-500 text-sm font-medium mb-6">Marking payout for {new Date(selectedPayout.month + "-01").toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} as paid.</p>

                                            <div className="space-y-4 relative">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Admin Comments / Notes</label>
                                                    <textarea
                                                        rows="3"
                                                        value={adminNotes}
                                                        onChange={(e) => setAdminNotes(e.target.value)}
                                                        placeholder="Enter any transaction details or notes..."
                                                        className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:border-[#24ADD7] transition-all resize-none"
                                                    ></textarea>
                                                </div>

                                                <div className="flex gap-3 pt-4">
                                                    <button
                                                        onClick={() => setShowSettleModal(false)}
                                                        className="flex-1 px-6 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-200 transition-all"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdatePayoutStatus(selectedPayout, 'paid', adminNotes)}
                                                        disabled={payoutLoading}
                                                        className="flex-1 px-6 py-4 bg-green-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-green-700 transition-all shadow-lg shadow-green-200/40 disabled:opacity-50"
                                                    >
                                                        {payoutLoading ? "Processing..." : "Mark as Paid"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="px-8 py-3 bg-slate-50/50 border-b border-slate-100 flex gap-2">
                                    {['all', 'pending', 'paid'].map((filter) => (
                                        <button
                                            key={filter}
                                            onClick={() => setPayoutStatusFilter(filter)}
                                            className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${payoutStatusFilter === filter
                                                ? 'bg-slate-800 text-white shadow-md'
                                                : 'bg-white text-slate-400 hover:bg-slate-100 border border-slate-100'
                                                }`}
                                        >
                                            {filter}
                                        </button>
                                    ))}
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Month</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Tasks</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Agent Share (35%)</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Comments</th>
                                                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {payouts?.length > 0 ? payouts
                                                .filter(p => payoutStatusFilter === 'all' || p.status === payoutStatusFilter)
                                                .map((payout, idx) => (
                                                    <tr key={payout._id || idx} className="hover:bg-slate-50/50 transition-colors group">
                                                        <td className="px-8 py-5">
                                                            <p className="text-sm font-bold text-slate-700">
                                                                {new Date(payout.month + "-01").toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                                                            </p>
                                                        </td>
                                                        <td className="px-8 py-5 text-sm font-bold text-slate-600">{payout.totalTasks} Duties</td>
                                                        <td className="px-8 py-5 text-sm font-black text-green-600">₹{payout.agentShare}</td>
                                                        <td className="px-8 py-5">
                                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${payout.status === 'paid' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
                                                                {payout.status}
                                                            </span>
                                                            {payout.paymentDate && (
                                                                <p className="text-[9px] text-slate-400 mt-1">Paid on: {new Date(payout.paymentDate).toLocaleDateString()}</p>
                                                            )}
                                                        </td>
                                                        <td className="px-8 py-5">
                                                            <p className="text-xs font-bold text-slate-500 italic max-w-[200px] truncate" title={payout.adminNotes}>
                                                                {payout.adminNotes || "—"}
                                                            </p>
                                                        </td>
                                                        <td className="px-8 py-5">
                                                            {payout.status === 'pending' ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedPayout(payout);
                                                                        setAdminNotes("");
                                                                        setShowSettleModal(true);
                                                                    }}
                                                                    className="px-4 py-2 bg-[#24ADD7] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#1c98bf] transition-all shadow-md shadow-[#24ADD7]/10"
                                                                >
                                                                    Settle Payout
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => navigate(`/services/payout-details/${payout._id}`)}
                                                                    className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
                                                                >
                                                                    View Details
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )) : (
                                                <tr>
                                                    <td colSpan="5" className="px-8 py-20 text-center text-slate-400 font-bold italic">No payout records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
